import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { StoreSettings, PassSettings } from '../api/types';
import { fetchStoreSettingsBySlug } from '../api/config';
import {useSearchParams} from "react-router-dom";

// Default pass settings (fallback when passSettings is missing)
export const DEFAULT_PASS_SETTINGS: PassSettings = {
  tenantId: '',
  style: 'default',
  heroText: 'GRINDIE',
  showPoints: true,
  showLastUpdate: false,
  backgroundColor: '#A9C191',
  foregroundColor: '#FFFFFF',
  labelColor: '#FFFFFF',
  logoUrl: '/icons/logo.svg',
  stripUrl: '',
  backgroundUrl: '/images/background.png',
  lblMemberName: 'Member',
  lblMembershipLvl: 'Membership Level',
  lblLastUpdate: 'Last Update',
  lblPoints: 'Points',
};

interface StoreSettingsContextType {
  storeSettings: StoreSettings | null;
  passSettings: PassSettings;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const StoreSettingsContext = createContext<StoreSettingsContextType | undefined>(undefined);

interface StoreSettingsProviderProps {
  children: ReactNode;
}

export const StoreSettingsProvider: React.FC<StoreSettingsProviderProps> = ({ children }) => {
  const [storeSettings, setStoreSettings] = useState<StoreSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  // Preload an image and resolve when loaded (or reject on error)
  const preloadImage = (url?: string | null) => {
    return new Promise<void>((resolve) => {
      if (!url) return resolve();
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve(); // don't block on errors
      img.src = url;
    });
  };

  // Resolve when the given promise completes OR when timeout elapses, whichever comes first
  const raceWithTimeout = (promise: Promise<unknown>, ms: number): Promise<void> => {
    return Promise.race([
      promise.then(() => undefined).catch(() => undefined),
      new Promise<void>((resolve) => setTimeout(resolve, ms)),
    ]) as Promise<void>;
  };

  const loadStoreSettings = async ( slug: string) => {
    try {
      setLoading(true);
      setError(null);
      const settings = await fetchStoreSettingsBySlug(slug);

      // Update the browser tab title as soon as we have the settings
      if (settings?.text && settings.text.trim()) {
        document.title = settings.text.trim();
      }

      // Preload critical assets before first paint to avoid FOUC
      const preloadAll = Promise.all([
        preloadImage(settings?.logoUrl),
        preloadImage(settings?.backgroundUrl),
      ]);
      await raceWithTimeout(preloadAll, 1500);

      setStoreSettings(settings);
    } catch (err) {
      console.error('Failed to fetch store settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load store settings');
      // Set default values as fallback
      setStoreSettings({
        tenantId: '',
        logoUrl: '/icons/logo.svg',
        text: 'Membership',
        backgroundUrl: '/images/background.png',
        slug: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const refetch = async () => {
    const store = searchParams.get("store")
    if (store) {
      await loadStoreSettings(store);
    }

  };

  useEffect(() => {
    // console.log("loadStoreSettings ...")
    const store = searchParams.get("store")
    if (store) {
      loadStoreSettings(store);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Keep document.title in sync if settings change later (only when we have a non-empty name)
  useEffect(() => {
    if (storeSettings?.text && storeSettings.text.trim()) {
      document.title = storeSettings.text.trim();
    }
  }, [storeSettings?.text]);

  // After loading finishes, if no store name is provided, fall back to the default title
  useEffect(() => {
    if (!loading && !(storeSettings?.text && storeSettings.text.trim())) {
      document.title = 'Membership Hub ';
    }
  }, [loading, storeSettings?.text]);

  // Merge passSettings from API with defaults
  const passSettings: PassSettings = {
    ...DEFAULT_PASS_SETTINGS,
    ...storeSettings?.passSettings,
    // Ensure colors have fallbacks if empty strings
    backgroundColor: storeSettings?.passSettings?.backgroundColor || DEFAULT_PASS_SETTINGS.backgroundColor,
    foregroundColor: storeSettings?.passSettings?.foregroundColor || DEFAULT_PASS_SETTINGS.foregroundColor,
    labelColor: storeSettings?.passSettings?.labelColor || DEFAULT_PASS_SETTINGS.labelColor,
    // Ensure URLs have fallbacks if empty strings
    logoUrl: storeSettings?.passSettings?.logoUrl || DEFAULT_PASS_SETTINGS.logoUrl,
    stripUrl: storeSettings?.passSettings?.stripUrl || DEFAULT_PASS_SETTINGS.stripUrl,
    backgroundUrl: storeSettings?.passSettings?.backgroundUrl || DEFAULT_PASS_SETTINGS.backgroundUrl,
  };

  const value: StoreSettingsContextType = {
    storeSettings,
    passSettings,
    loading,
    error,
    refetch,
  };

  // Always render children - let App.tsx handle the loading skeleton
  return (
    <StoreSettingsContext.Provider value={value}>
      {children}
    </StoreSettingsContext.Provider>
  );
};

export const useStoreSettings = (): StoreSettingsContextType => {
  const context = useContext(StoreSettingsContext);
  if (context === undefined) {
    throw new Error('useStoreSettings must be used within a StoreSettingsProvider');
  }
  return context;
};
